/* eslint-disable react-hooks/exhaustive-deps */
import { IconButton, Typography, Theme, Box, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/styles';
import { VideoCamOff } from '../../Assets/VideoCamOff';
import { StatusComponent } from './StatusComponent';
import { getSevereLocations, SevereThreat } from '../../features/dash/dashSlice';
import { useEffect, useState } from 'react';
import { useAppSelector } from '../../app/hooks';
import { Tooltip } from '@perry-weather/component-library';
import { GetThreatIcon } from '../../features/ThreatHelper';
import { Expand } from '../../Assets/Expand';
import WxImageModal from '../WeatherStation/WxImageModal';

interface WxImageProps {
  selectable?: boolean;
  expandable?: boolean;
  showStatus?: boolean;
  locationId?: string;
  style?: React.CSSProperties;
  weatherStationImageUrl?: string;
}

const useStyles = (theme: Theme): { [key: string]: React.CSSProperties } => ({
  imageContainer: {
    boxSizing: 'border-box',
    boxShadow: '20px 25px 40px rgba(0, 0, 0, 0.1)',
    position: 'relative',
    display: 'flex',
    justifyContent: 'flex-end',
    borderRadius: '8px',
    border: '1px solid',
    borderColor: theme.palette.border.main,
    backgroundColor: theme.palette.background.paper,
    overflow: 'hidden',
  },
  image: {
    borderRadius: '8px',
    width: '100%',
    opacity: 0.3,
    objectFit: 'cover',
  },
  imageDefault: {
    borderRadius: '8px',
    width: '100%',
    height: '100%',
    opacity: 1,
    objectFit: 'cover',
  },
  imageSelected: {
    width: '100%',
    borderRadius: '8px',
    border: '2px solid',
    borderColor: theme.palette.action.selected,
    objectFit: 'cover',
  },
  imagePlaceholder: {
    width: '100%',
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  selected: {
    border: '2px solid',
    borderColor: theme.palette.action.selected,
  },
});
export function WxImage(props: WxImageProps) {
  const theme = useTheme();
  const classes = useStyles(theme);
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));

  const { locationId, style, expandable = false, showStatus = false, selectable = false, weatherStationImageUrl } = props;
  const [imgError, setImgError] = useState(false);
  const [threats, setThreats] = useState<SevereThreat[]>([]);
  const [delayStatus, setDelayStatus] = useState(false);
  const [expandModalOpen, setExpandModalOpen] = useState(false);

  const severeLocations = useAppSelector(getSevereLocations);

  const GetStatusComponent = () => {
    let count = 0;
    let subCount = 0;

    const threatIcons = threats.map(threat => {
      count++;
      return count <= 3 ? (
        <Tooltip
          key={threat.shortName}
          title={
            threat.longName.length > 1 ? (
              <Box padding='0 16px'>
                <ul style={{ listStyle: 'initial' }}>
                  {threat.longName.map(longname => (
                    <li key={++subCount}>
                      <Typography variant='body2'>{longname}</Typography>
                    </li>
                  ))}
                </ul>
              </Box>
            ) : (
              <Typography variant='body2'>{threat.longName}</Typography>
            )
          }>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {' '}
            {GetThreatIcon(threat.shortName, '#fff', 20, 20)}{' '}
          </div>
        </Tooltip>
      ) : null;
    });

    let additionalThreatsDisplay: string[] = [];

    if (threats.length > 3)
      additionalThreatsDisplay = [...threats.slice(3).map(x => x.longName.join(', '))].join(', ').split(', ');

    return (
      <StatusComponent status='danger' style={{ position: 'absolute', top: 8, right: 8 }}>
        {threatIcons}
        {threats.length > 3 && (
          <Tooltip
            title={
              <Box padding='0 16px'>
                {additionalThreatsDisplay.length > 1 ? (
                  <ul style={{ listStyle: 'initial' }}>
                    {additionalThreatsDisplay.map(threat => (
                      <li key={threat}>
                        <Typography variant='body2'>{threat}</Typography>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <Typography variant='body2'>{additionalThreatsDisplay[0]}</Typography>
                )}
              </Box>
            }>
            <Box height={20}>
              <Typography variant='h5' lineHeight={0.75}>
                ...
              </Typography>
            </Box>
          </Tooltip>
        )}
      </StatusComponent>
    );
  };

  useEffect(() => {
    if (locationId && severeLocations) {
      let threatsForLoc = severeLocations[locationId];

      if (threatsForLoc && threatsForLoc.length > 0) {
        setDelayStatus(true);
        setThreats(threatsForLoc);
      } else {
        setDelayStatus(false);
        setThreats([]);
      }
    }
  }, [severeLocations, locationId]);

  const display = (
    <Box sx={classes.imageContainer} style={style}>
      {threats.length > 0 && GetStatusComponent()}

      {weatherStationImageUrl && !imgError ? (
        !selectable ? (
          <img
            draggable='false'
            style={classes.imageDefault}
            src={weatherStationImageUrl}
            alt={''}
            onError={() => setImgError(true)}
          />
        ) : (
          <img
            draggable='false'
            style={classes.image}
            src={weatherStationImageUrl}
            alt={''}
            onError={() => setImgError(true)}
          />
        )
      ) : (
        <Box sx={classes.imagePlaceholder}>
          <VideoCamOff width={36} height={32} color={theme.palette.text.disabled} />
        </Box>
      )}
    </Box>
  );

  if (showStatus) {
    return (
      <Tooltip
        title={!delayStatus ? <Typography>{'No policy warnings'}</Typography> : ''}
        arrow
        placement='top'
        enterDelay={300}
        leaveDelay={200}>
        {display}
      </Tooltip>
    );
  } else {
    return (
      <Box sx={classes.imageContainer} style={style}>
        {display}
        {!isSm && expandable && weatherStationImageUrl && (
          <IconButton sx={{ position: 'absolute', bottom: 5, right: 5 }} onClick={() => setExpandModalOpen(true)}>
            <Expand color={theme.palette.primary.main} />
          </IconButton>
        )}
        {!isSm && expandable && weatherStationImageUrl && (
          <WxImageModal
            src={weatherStationImageUrl}
            modalState={{ open: expandModalOpen, handleClose: () => setExpandModalOpen(false), handleOpen: () => {} }}
          />
        )}
      </Box>
    );
  }
}